import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { notificationsAPI } from '../../lib/api';
import { formatRelativeDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
    Bell, Check, CheckCheck, FileText, PenTool, Star, Clock,
    AlertTriangle, Info, Filter, X, ChevronRight, Inbox
} from 'lucide-react';

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.04 } } };

// Priority categorization — derive de la categorie BACKEND ('category' field)
// avec fallback heuristique sur le type si jamais le backend ne fournit pas le champ.
const PRIORITY_CONFIG = {
    urgent: {
        label: 'Urgent',
        icon: AlertTriangle,
        color: 'text-red-400',
        bgActive: 'bg-red-500/15 border-red-500/30 text-red-400',
        dotColor: 'bg-red-500',
    },
    important: {
        label: 'Important',
        icon: Star,
        color: 'text-[#FF914D]',
        bgActive: 'bg-[#FF914D]/15 border-[#FF914D]/30 text-[#FF914D]',
        dotColor: 'bg-[#FF914D]',
    },
    info: {
        label: 'Informatif',
        icon: Info,
        color: 'text-blue-400',
        bgActive: 'bg-blue-500/15 border-blue-500/30 text-blue-400',
        dotColor: 'bg-blue-500',
    }
};

// Fallback heuristique si le backend ne retourne pas `category`
const FALLBACK_CATEGORY = {
    new_offer: 'urgent', offer_accepted: 'urgent', signature_pending: 'urgent',
    visit_reminder: 'urgent', feedback_request: 'urgent', ticket_reply: 'urgent',
    offer_rejected: 'important', signature_complete: 'important',
    new_mandate: 'important', mandate_update: 'important',
    feedback_received: 'important', certification_passed: 'important',
};

const getNotifPriority = (notif) => {
    // Le backend renvoie `category` directement. Sinon on devine depuis le type.
    if (notif.category) return notif.category;
    return FALLBACK_CATEGORY[notif.notification_type] || 'info';
};

const getNotifIcon = (type) => {
    switch (type) {
        case 'signature_complete':
        case 'signature_pending': return PenTool;
        case 'rating_received': return Star;
        case 'reminder': return Clock;
        case 'mandate': return FileText;
        default: return Bell;
    }
};

const getIconStyle = (type) => {
    switch (type) {
        case 'signature_complete': return 'bg-green-500/20 text-green-400';
        case 'signature_pending': return 'bg-red-500/20 text-red-400';
        case 'rating_received': return 'bg-yellow-500/20 text-yellow-400';
        case 'reminder': return 'bg-[#FF914D]/20 text-[#FF914D]';
        case 'mandate': return 'bg-blue-500/20 text-blue-400';
        default: return 'bg-[#681793]/20 text-[#681793]';
    }
};

function NotificationsPage() {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState('all');

    useEffect(() => { loadNotifications(); }, []);

    const loadNotifications = async () => {
        try {
            const response = await notificationsAPI.getAll();
            setNotifications(response.data);
        } catch {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const markAsRead = async (id) => {
        try {
            await notificationsAPI.markRead(id);
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
        } catch {
            toast.error('Erreur');
        }
    };

    const markAllAsRead = async () => {
        try {
            await notificationsAPI.markAllRead();
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            toast.success('Toutes les notifications marquées comme lues');
        } catch {
            toast.error('Erreur');
        }
    };

    const unreadCount = notifications.filter(n => !n.is_read).length;

    // Priority counts
    const priorityCounts = useMemo(() => {
        const counts = { all: notifications.length, urgent: 0, important: 0, info: 0 };
        notifications.forEach(n => {
            const p = getNotifPriority(n);
            counts[p]++;
        });
        return counts;
    }, [notifications]);

    const unreadPriorityCounts = useMemo(() => {
        const counts = { all: unreadCount, urgent: 0, important: 0, info: 0 };
        notifications.filter(n => !n.is_read).forEach(n => {
            const p = getNotifPriority(n);
            counts[p]++;
        });
        return counts;
    }, [notifications, unreadCount]);

    // Filtered notifications
    const filteredNotifications = useMemo(() => {
        if (activeFilter === 'all') return notifications;
        return notifications.filter(n => getNotifPriority(n) === activeFilter);
    }, [notifications, activeFilter]);

    if (loading) {
        return (
            <div className="space-y-6" data-testid="notifications-page-loading">
                <Skeleton className="h-12 w-64 bg-muted" />
                <div className="flex gap-3">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-24 bg-muted rounded-full" />)}
                </div>
                <div className="space-y-3">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20 w-full bg-muted rounded-xl" />)}
                </div>
            </div>
        );
    }

    return (
        <motion.div
            className="space-y-6"
            data-testid="notifications-page"
            initial="hidden" animate="visible" variants={stagger}
        >
            {/* Header */}
            <motion.div variants={fadeIn} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <Bell className="w-6 h-6 text-[#FF914D]" />
                        <h1 className="text-2xl sm:text-3xl font-black text-foreground">Notifications</h1>
                        {unreadCount > 0 && (
                            <span className="px-2 py-0.5 rounded-full bg-[#FF914D] text-white text-xs font-bold">{unreadCount}</span>
                        )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                        {unreadCount > 0 ? `${unreadCount} non lue${unreadCount > 1 ? 's' : ''}` : 'Toutes lues'}
                    </p>
                </div>
                {unreadCount > 0 && (
                    <Button variant="outline" onClick={markAllAsRead}
                        className="border-border text-muted-foreground hover:text-white hover:bg-card"
                        data-testid="mark-all-read-button">
                        <CheckCheck className="w-4 h-4 mr-2" />
                        Tout marquer comme lu
                    </Button>
                )}
            </motion.div>

            {/* Desktop: Two-column layout / Mobile: Single column */}
            <div className="lg:grid lg:grid-cols-12 lg:gap-6">
                
                {/* Priority Sidebar - Desktop */}
                <motion.div variants={fadeIn} className="lg:col-span-3">
                    {/* Mobile: horizontal chips / Desktop: vertical sidebar */}
                    <div className="lg:sticky lg:top-24">
                        {/* Mobile filter chips */}
                        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide lg:hidden" data-testid="mobile-priority-chips">
                            <FilterChip label="Tout" count={priorityCounts.all} unread={unreadPriorityCounts.all}
                                active={activeFilter === 'all'} onClick={() => setActiveFilter('all')} />
                            {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                                <FilterChip key={key} label={cfg.label} count={priorityCounts[key]}
                                    unread={unreadPriorityCounts[key]} active={activeFilter === key}
                                    onClick={() => setActiveFilter(key)} icon={cfg.icon} color={cfg.color} />
                            ))}
                        </div>

                        {/* Desktop sidebar */}
                        <div className="hidden lg:block bg-card rounded-2xl border border-border p-4 space-y-2" data-testid="desktop-priority-sidebar">
                            <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold px-3 mb-3 flex items-center gap-2">
                                <Filter className="w-3.5 h-3.5" /> Priorité
                            </h3>
                            <SidebarItem label="Toutes" count={priorityCounts.all} unread={unreadPriorityCounts.all}
                                active={activeFilter === 'all'} onClick={() => setActiveFilter('all')} />
                            {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                                <SidebarItem key={key} label={cfg.label} count={priorityCounts[key]}
                                    unread={unreadPriorityCounts[key]} active={activeFilter === key}
                                    onClick={() => setActiveFilter(key)} icon={cfg.icon}
                                    dotColor={cfg.dotColor} activeClass={cfg.bgActive} />
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Notifications List */}
                <motion.div variants={fadeIn} className="lg:col-span-9 mt-4 lg:mt-0">
                    {activeFilter !== 'all' && (
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-sm text-muted-foreground">
                                {filteredNotifications.length} notification{filteredNotifications.length !== 1 ? 's' : ''}
                            </span>
                            <button onClick={() => setActiveFilter('all')} className="text-xs text-[#FF914D] hover:underline flex items-center gap-1">
                                <X className="w-3 h-3" /> Réinitialiser
                            </button>
                        </div>
                    )}

                    <div className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden">
                        {filteredNotifications.length === 0 ? (
                            <EmptyState filter={activeFilter} />
                        ) : (
                            <div className="divide-y divide-border">
                                {filteredNotifications.map((notification, idx) => (
                                    <NotificationRow key={notification.id} notification={notification}
                                        idx={idx} onMarkRead={markAsRead} />
                                ))}
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </motion.div>
    );
}

// Sub-components

function FilterChip({ label, count, unread, active, onClick, icon: Icon, color }) {
    return (
        <button onClick={onClick}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                active ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white shadow-lg shadow-[#681793]/20'
                : 'bg-card border border-border text-muted-foreground hover:text-white hover:border-white/20'
            }`} data-testid={`filter-chip-${label.toLowerCase()}`}>
            {Icon && <Icon className="w-3 h-3" />}
            <span>{label}</span>
            {count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${active ? 'bg-white/20' : 'bg-white/5'}`}>
                    {count}
                    {unread > 0 && !active && <span className="text-[#FF914D]"> ({unread})</span>}
                </span>
            )}
        </button>
    );
}

function SidebarItem({ label, count, unread, active, onClick, icon: Icon, dotColor, activeClass }) {
    return (
        <button onClick={onClick} data-testid={`sidebar-${label.toLowerCase()}`}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                active ? (activeClass || 'bg-gradient-to-r from-[#681793]/20 to-[#FF914D]/20 border border-[#681793]/30 text-white')
                : 'text-muted-foreground hover:text-white hover:bg-white/5'
            }`}>
            {dotColor && <span className={`w-2 h-2 rounded-full ${dotColor} shrink-0`} />}
            {Icon && !dotColor && <Icon className="w-4 h-4 shrink-0" />}
            <span className="flex-1 text-left">{label}</span>
            <div className="flex items-center gap-1.5">
                {unread > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full bg-[#FF914D] text-white text-[10px] font-bold min-w-[18px] text-center">
                        {unread}
                    </span>
                )}
                <span className="text-xs text-muted-foreground">{count}</span>
            </div>
        </button>
    );
}

function NotificationRow({ notification, idx, onMarkRead }) {
    const Icon = getNotifIcon(notification.notification_type);
    const priority = getNotifPriority(notification);
    const prioConfig = PRIORITY_CONFIG[priority];

    return (
        <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.02 }}
            className={`flex items-start gap-4 p-4 lg:p-5 transition-colors group ${
                !notification.is_read ? 'bg-[#681793]/5' : 'hover:bg-white/[0.02]'
            }`}
            data-testid={`notification-item-${notification.id}`}
        >
            {/* Icon */}
            <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${getIconStyle(notification.notification_type)}`}>
                <Icon className="w-5 h-5 lg:w-6 lg:h-6" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <p className={`font-medium text-sm lg:text-base ${!notification.is_read ? 'text-white' : 'text-muted-foreground'}`}>
                                {notification.title}
                            </p>
                            {/* Priority badge - Desktop only */}
                            <Badge className={`hidden lg:inline-flex text-[10px] px-1.5 py-0 border ${prioConfig.bgActive}`}>
                                {prioConfig.label}
                            </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                            {notification.message}
                        </p>
                        <p className="text-xs text-white/30 mt-1.5">
                            {formatRelativeDate(notification.created_at)}
                        </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                        {!notification.is_read && (
                            <Button variant="ghost" size="sm" onClick={() => onMarkRead(notification.id)}
                                className="text-muted-foreground hover:text-green-400 hover:bg-green-500/10 h-8 w-8 p-0"
                                data-testid={`mark-read-${notification.id}`}>
                                <Check className="w-4 h-4" />
                            </Button>
                        )}
                        {notification.link && (
                            <ChevronRight className="w-4 h-4 text-white/15 group-hover:text-white/40 transition-colors hidden lg:block" />
                        )}
                    </div>
                </div>
            </div>

            {/* Unread indicator */}
            {!notification.is_read && (
                <div className="w-2 h-2 rounded-full bg-[#FF914D] shrink-0 mt-2 lg:mt-3" />
            )}
        </motion.div>
    );
}

function EmptyState({ filter }) {
    return (
        <div className="text-center py-16 px-6" data-testid="notifications-empty">
            <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
                <Inbox className="w-8 h-8 text-white/20" />
            </div>
            <p className="text-muted-foreground font-medium">
                {filter !== 'all' ? `Aucune notification ${PRIORITY_CONFIG[filter]?.label.toLowerCase() || ''}` : 'Aucune notification'}
            </p>
            <p className="text-sm text-white/30 mt-1">
                Vous serez notifié des événements importants ici
            </p>
        </div>
    );
}

export default NotificationsPage;
